import React, {
  useState,
  useRef,
  FC,
  ElementType,
  LegacyRef,
  KeyboardEventHandler,
  MouseEventHandler,
  ReactNode
} from 'react';
import classNames from 'classnames';

import { ComposedTheme, useTheme } from '@glu/theming';
import { Fade } from '@glu/transitions-react';
import { useTrapFocus } from '@glu/utilities-react';
import {
  CheckmarkCircleIcon,
  XCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InfoCircleAshIcon as InfoCircleIcon
} from '@glu/icons-react';
import { Button, TERTIARY } from '@glu/buttons-react';

import useKeyHandler from 'hooks/useKeyHandler';
import useModalFocus from 'hooks/useModalFocus';

import useStyles from './ModalNotification.styles';

interface Props {
  className?: string;
  dialogClassName?: string;
  closeButton?: boolean;
  children: ReactNode;
  footer?: ReactNode;
  focusPrimaryBtn?: boolean;
  hideBackdrop?: boolean;
  header?: ReactNode;
  icon?: ElementType;
  iconTitle?: string;
  mountOnEnter?: boolean;
  onHide?: (evt: unknown) => void;
  show?: boolean;
  suppressIcon?: boolean;
  // Too many prop type errors. Takes string or react component
  // eslint-disable-next-line react/forbid-prop-types
  transition?: ReactNode | string | unknown;
  unmountOnExit?: boolean;
  variant?: 'info' | 'success' | 'error' | 'warning';
  disableBackdropClick?: boolean;
  emulation?: boolean;
}

const ModalNotification: FC<Props> = ({
  children,
  className = 'modal',
  closeButton = true,
  dialogClassName = '',
  disableBackdropClick = false,
  emulation = false,
  focusPrimaryBtn = false,
  footer = null,
  header = null,
  hideBackdrop = null,
  icon = null,
  iconTitle = '',
  mountOnEnter = true,
  onHide = () => {},
  show = false,
  suppressIcon = true,
  transition: Transition = Fade,
  transition,
  unmountOnExit = true,
  variant = 'info'
}) => {
  const classes = useStyles();
  const {
    transitions: {
      duration: { short }
    }
  } = useTheme<ComposedTheme>();
  const [transitionIn, setTransitionIn] = useState(true);
  const modalRef = useRef<HTMLDivElement>();

  const transitionDone = (event: unknown) => {
    setTransitionIn(true);
    onHide(event);
  };

  const closeModal: MouseEventHandler<HTMLDivElement | HTMLButtonElement> = (event) => {
    if (typeof transition === 'string') {
      onHide(event);
      return;
    }

    setTransitionIn(false);
    setTimeout(() => transitionDone(event), short);
  };

  const getIconComponent = () => {
    if (icon) {
      const Icon = icon;
      return Icon;
    }
    switch (variant) {
      case 'success':
        return CheckmarkCircleIcon;
      case 'error':
        return ExclamationCircleIcon;
      case 'warning':
        return ExclamationTriangleIcon;
      default:
        return InfoCircleIcon;
    }
  };

  const showTransition = show && transitionIn;

  const transitionProps =
    typeof Transition === 'string'
      ? {}
      : {
          in: show && transitionIn,
          mountOnEnter,
          unmountOnExit
        };

  const IconComponent = getIconComponent();

  const getAriaLabel = () => 'Dialog';

  // Focus modal on open, focus previous element on close
  useModalFocus({ ref: modalRef, active: show, ignore: focusPrimaryBtn });

  // Escape key to close modal
  useKeyHandler({ keyEvent: 'keyup', keyCode: 27, callback: closeModal, active: show });

  const onKeyDown = useTrapFocus({ ref: modalRef, active: show });

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <section className={classes.modalComponent}>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <div
        ref={modalRef as LegacyRef<HTMLDivElement>}
        aria-label={getAriaLabel()}
        className={classNames(className, classes.root, { [classes.modalIn]: show })}
        data-qa="modal-notification"
        onKeyDown={onKeyDown as unknown as KeyboardEventHandler<HTMLElement>}
        role="dialog"
        tabIndex={-1}
      >
        {hideBackdrop ? null : (
          <Fade in={showTransition} mountOnEnter={mountOnEnter} unmountOnExit={unmountOnExit}>
            <div
              className={classes.backdrop}
              role="button"
              {...(disableBackdropClick
                ? {}
                : {
                    onClick: closeModal
                  })}
              tabIndex={0}
            />
          </Fade>
        )}
        <div className={classes.contentWrappper}>
          <Transition {...transitionProps}>
            <article
              className={classNames(dialogClassName, classes.dialog, 'dialog', { emulation })}
              role="document"
            >
              <div className={classNames(classes.content, classes[variant])}>
                {header && (
                  <header className={classes.header}>
                    {closeButton && (
                      <Button
                        className={classes.headerCloseBth}
                        onClick={closeModal}
                        type="button"
                        variant={TERTIARY}
                      >
                        <span aria-hidden="true">×</span>
                      </Button>
                    )}
                    {header}
                  </header>
                )}
                <section className={classes.modalBody}>
                  {!header && closeButton && (
                    <Button
                      className={classes.closeRounded}
                      onClick={closeModal}
                      type="button"
                      variant={TERTIARY}
                    >
                      <span className="sr-only">Close dialog</span>
                      <XCircleIcon aria-hidden title="Close dialog" />
                    </Button>
                  )}
                  {!suppressIcon && (
                    <IconComponent
                      aria-hidden
                      className={classes.checkmarkWrapper}
                      focusable="false"
                      title={iconTitle || variant}
                    />
                  )}
                  <div>{children}</div>
                </section>

                {footer && <footer className={classes.modalFooter}>{footer}</footer>}
              </div>
            </article>
          </Transition>
        </div>
      </div>
    </section>
  );
};

export default ModalNotification;
